/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Warning from '@mui/icons-material/Warning';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  selectIsInitialConfigRequestFinished,
  selectSalesforceBaseURL,
  selectTerminals,
  selectTickets,
  selectTicketsOptions,
} from '../../store';
import { fetchTickets } from '../../store/tickets/actions';
import { TicketsState } from '../../store/tickets/reducer';
import appTheme from '../common/theme/appTheme.json';
import StyledProps, { ThemeInterface } from '../common/theme/AppThemeInterface';
import TicketsTable from './TicketsTable';
import TicketsTablePaginationControls from './TicketsTablePaginationControls';
import { setMapHidden, TICKETS_PAGE_MAP_ID } from '../../store/maps/actions';

const theme: ThemeInterface = appTheme;

export function TicketsContainer() {
  const dispatch = useDispatch();

  const tickets: Readonly<TicketsState> = useSelector(selectTickets, shallowEqual);
  const ticketOptions = useSelector(selectTicketsOptions, shallowEqual);
  const salesforceBaseURL = useSelector(selectSalesforceBaseURL, shallowEqual);
  const terminals = useSelector(selectTerminals, shallowEqual);
  const isInitialConfigRequestFinished = useSelector(selectIsInitialConfigRequestFinished, shallowEqual);

  useEffect(() => {
    dispatch(fetchTickets());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setMapHidden(TICKETS_PAGE_MAP_ID)(true));

    return () => {
      dispatch(setMapHidden(TICKETS_PAGE_MAP_ID)(false));
    };
  }, [dispatch]);

  if (!isInitialConfigRequestFinished) {
    return null;
  }

  return (
    <TicketsTableView>
      {tickets.error && !tickets.loading ? (
        <div className="error__container">
          <div className="error__message__container">
            <Warning />
            <div>Error Retrieving Tickets</div>
            <Button
              color="primary"
              variant="text"
              sx={{ zIndex: 5 }}
              onClick={() => {
                dispatch(fetchTickets(ticketOptions));
              }}
            >
              RETRY
            </Button>
          </div>
        </div>
      ) : null}
      <StyledBackdrop open={!!tickets.loading}>
        <Box position="absolute" display="flex" width="100%" height="100vh" justifyContent="center" alignItems="center">
          <CircularProgress className="progress" />
        </Box>
      </StyledBackdrop>

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height="calc(100vh - 114px)">
        <Box>
          <TicketsTable tickets={tickets} terminals={terminals} salesforceBaseURL={salesforceBaseURL} />
        </Box>
        <Box>
          <TicketsTablePaginationControls theme={theme} tickets={tickets} />
        </Box>
      </Box>
    </TicketsTableView>
  );
}

export default TicketsContainer;

export const TicketsTableView = styled.div`
  width: 100%;
  height: 100%;

  .error__message__container {
    position: absolute;
    width: 100%;
    top: 125px;
    height: 100px;
    text-align: center;
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  }

  .ArrowUpIcon {
    height: 12px;
    width: 12px;
  }

  .ArrowDownIcon {
    height: 12px;
    width: 12px;
  }

  .SortIcon {
    height: 12px;
  }
`;
const StyledBackdrop = styled(Backdrop)`
  top: 103px;
  left: 52px;
  z-index: 99999;
`;
